import {Suspense, useRef} from 'react'

import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import {useMediaQuery} from '@s-ui/react-hooks'
import {
  MoleculeDrawer,
  MoleculeDrawerOverlay,
  moleculeDrawerPlacements,
  moleculeDrawerSizes
} from '@s-ui/react-molecule-drawer'
import {useRouter} from '@s-ui/react-router'
import {useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import ComparatorContent from './ComparatorContent.js'

const BASE_CLASS = 'mt-ComparatorButton'

const ComparatorDrawer = ({
  isOpen = false,
  onClose,
  comparatorSelectedAds = [],
  onRemoveSelectedAd,
  clearSelectedAds,
  extendedTrackingData,
  closeOnOutsideClick
}) => {
  const {domain} = useSuiContext()
  const {push} = useRouter()
  const {track} = useAnalyticsTrack()

  const drawerRef = useRef()

  const config = domain.get('config')
  const {COMPARATOR_PATH_PATTERN} = config.get('page')
  const {
    HIT_ORIGINS: {LIST_ADS_COMPARISON_MENU},
    TRACKING_EVENT_NAMES: {AD_TO_COMPARE_SEE_RESULTS_CLICKED}
  } = config.get('tracking')
  const isMobile = config.get('isMobile')
  const isHigherThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !isMobile
  })

  const _onRemoveSelectedAd = ad => {
    onRemoveSelectedAd(ad)
    // If it was last ad, close the drawer
    if (comparatorSelectedAds.length === 1) {
      onClose?.()
    }
  }

  const _clearSelectedAds = () => {
    onClose?.()
    clearSelectedAds()
  }

  const _onClickShow = async () => {
    await trackClickingResults()
    onClose?.()
    push({
      pathname: COMPARATOR_PATH_PATTERN,
      query: {ids: comparatorSelectedAds.map(ad => ad.id).join(',')},
      state: {ads: comparatorSelectedAds, listType: extendedTrackingData?.products?.[0]?.list_type}
    })
  }

  const trackClickingResults = async () => {
    const adComparatorTrackingData = await domain.get('get_event_tracking_data_use_case').execute({
      event: AD_TO_COMPARE_SEE_RESULTS_CLICKED,
      origin: LIST_ADS_COMPARISON_MENU,
      listType: extendedTrackingData?.products?.[0]?.list_type,
      ads: comparatorSelectedAds
    })
    track(adComparatorTrackingData)
  }

  if (!isOpen) {
    return null
  }

  return (
    <Suspense fallback={<></>}>
      <MoleculeDrawerOverlay isVisible={true}>
        <div className={`${BASE_CLASS}-drawerWrapper`}>
          <MoleculeDrawer
            ref={drawerRef}
            isOpen={true}
            onClose={onClose}
            placement={isHigherThanMobile ? moleculeDrawerPlacements.RIGHT : moleculeDrawerPlacements.BOTTOM}
            size={moleculeDrawerSizes.AUTO}
            closeOnOutsideClick={closeOnOutsideClick}
          >
            <ComparatorContent
              comparatorSelectedAds={comparatorSelectedAds}
              onRemoveSelectedAd={_onRemoveSelectedAd}
              clearSelectedAds={_clearSelectedAds}
              onClickShow={_onClickShow}
              onClose={onClose}
            />
          </MoleculeDrawer>
        </div>
      </MoleculeDrawerOverlay>
    </Suspense>
  )
}

ComparatorDrawer.displayName = 'ComparatorDrawer'

ComparatorDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  comparatorSelectedAds: PropTypes.array,
  onRemoveSelectedAd: PropTypes.func,
  clearSelectedAds: PropTypes.func,
  extendedTrackingData: PropTypes.object,
  closeOnOutsideClick: PropTypes.bool
}

export default ComparatorDrawer
